import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Quote } from 'lucide-react';

const testimonials = [
  {
    company: "SAKARA",
    name: "Sarah B.",
    role: "Director of Client Experience",
    text: "The Peer2Learn team is always flexible with process changes and commits 100% to making every interaction the best our clients have ever had on a daily basis.",
  },
  {
    company: "EDMOSS",
    name: "Tolu Adebayo",
    role: "Student, Computer Science",
    text: "This platform helped me find the perfect tutor for my programming needs. The flexible payment options made it affordable and easy to stay consistent!",
  },
  {
    company: "TECH-AFRICA",
    name: "Kofi Mensah",
    role: "Fullstack Developer",
    text: "The mentorship here is world-class. I was able to bridge the gap between theory and practical industry standards in months. Highly recommended.",
  }
];

const TestimonialSlider = () => {
  const [index, setIndex] = useState(0);
  const duration = 5; // seconds

  // Handle auto-next logic
  const handleNext = () => {
    setIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <div className="w-full min-h-[600px] flex flex-col items-center justify-center bg-[#FCFAF7] py-16 px-6 font-sans select-none overflow-hidden">
      
      {/* SECTION HEADING */}
      <div className="mb-12 text-center">
        <h1 className="text-3xl md:text-4xl font-bold tracking-[0.2em] text-slate-800 uppercase relative inline-block">
          Testimonials
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-[#1976D2] rounded-full"></span>
        </h1>
        <p className="mt-4 text-slate-500 text-sm font-medium uppercase tracking-widest">What our community says</p>
      </div>

      {/* Main Container */}
      <div className="max-w-5xl w-full flex flex-col md:flex-row items-center justify-center gap-12 md:gap-20">
        
        {/* IMAGE/ICON SECTION */}
        <div className="relative shrink-0">
          {/* Decorative Ring */}
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border-2 border-dashed border-[#1976D2]/30 -m-4"
          />
          
          {/* Main Blue Ring */}
          <div className="w-48 h-48 md:w-64 md:h-64 rounded-full border-[10px] md:border-[14px] border-[#1976D2] flex items-center justify-center bg-white z-10 shadow-xl relative">
            <div className="w-[85%] h-[85%] rounded-full flex items-center justify-center bg-[#F1F7FE]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  transition={{ duration: 0.4 }}
                >
                  <User size={80} strokeWidth={1} className="text-[#1976D2] md:hidden" />
                  <User size={110} strokeWidth={1} className="text-[#1976D2] hidden md:block" />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          
          {/* Small Intersecting Bubble Ring (Visible only on desktop) */}
          <div className="absolute -right-8 top-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-[8px] border-[#1976D2] bg-[#FCFAF7] z-20 hidden md:block shadow-lg" />
        </div>

        {/* TEXT CONTENT SECTION */}
        <div className="flex-1 text-center md:text-left relative max-w-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="space-y-6"
            >
              <div className="relative">
                {/* Visual Quote Icon */}
                <Quote className="text-[#1976D2]/10 absolute -top-8 -left-8 w-16 h-16 md:w-20 md:h-20" />
                
                <p className="text-lg md:text-xl leading-relaxed text-slate-700 font-medium italic relative z-10">
                  "{testimonials[index].text}"
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 inline-block md:block">
                <h4 className="font-bold text-lg md:text-xl text-slate-900">{testimonials[index].name}</h4>
                <p className="text-[#1976D2] text-xs md:text-sm font-bold uppercase tracking-[0.15em] mt-1">
                  {testimonials[index].role}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* PROGRESS BAR SECTION */}
      <div className="mt-16 w-full max-w-md h-[4px] bg-slate-200 rounded-full flex relative items-center overflow-hidden">
        {testimonials.map((_, i) => (
          <div key={i} className="flex-1 h-full relative flex items-center bg-transparent border-r border-white last:border-0">
            {/* The Filler */}
            {i === index && (
              <motion.div
                key={index}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: duration, ease: "linear" }}
                onAnimationComplete={handleNext}
                className="h-full bg-[#1976D2]"
              />
            )}
            
            {/* Completed Segments */}
            {i < index && <div className="absolute inset-0 bg-[#1976D2]" />}
          </div>
        ))}
      </div>

      {/* Manual Dots for quick navigation */}
      <div className="flex gap-3 mt-6">
        {testimonials.map((_, i) => (
          <button 
            key={i} 
            onClick={() => setIndex(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === index ? 'bg-[#1976D2] w-6' : 'bg-slate-300'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialSlider;